import React, { useState, FormEvent, ChangeEvent } from 'react';
import { Card, CardContent, CardFooter, CardHeading } from '@components/layout/Card'
import { Button } from "@UILibrary/Button"
import { Text } from '@UILibrary/Text'
import { Stack } from '@UILibrary/Stack'
import { useBreakpoints } from '@UILibrary/hooks/useBreakpoints'
import styles from './AttendanceTracker.module.sass'

// Type definitions
interface AttendanceTrackerProps {
  eventSlug: string;
  workshopName: string;
}

interface ApiResponse {
  success?: boolean;
  redirect_url?: string;
  errors?: string[];
  error?: string;
}

export const AttendanceTracker: React.FC<AttendanceTrackerProps> = ({ eventSlug, workshopName }) => {
  const [email, setEmail] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!email.trim()) {
      return;
    }

    setLoading(true);
    setErrors([]);

    const formData = new FormData();
    formData.append('email', email.trim());

    try {
      const response = await fetch(`/luma/events/${eventSlug}/join`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });

      const data: ApiResponse = await response.json();

      if (response.ok && data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        setErrors(data.errors || [data.error || 'An error occurred']);
      }
    } catch (error) {
      setErrors(['An error occurred. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
  };

  return (
    <div className={styles.container}>
      <div className={styles.backgroundImage} />

      <Stack
        vertical
        alignItems="center"
        justifyContent="center"
        padding="l"
        className={styles.content}
      >
        <div className={styles.cardContainer}>
          <Card className="">
            <CardHeading className="">
              <div className={styles.cardHeader}>
                <Stack vertical spacing="m" alignItems="center">
                  <img
                    src="/assets/ffp/ffp-workshop-image.jpeg"
                    alt="FFP Workshop"
                    className={styles.workshopImage}
                  />
                  <Text heading2a colour="default" as="h1" className={styles.title}>
                    {workshopName || '🚨 No workshop name provided'}
                  </Text>
                  <Text body colour="default" className={styles.subtitle}>
                    Enter your work email to join
                  </Text>
                </Stack>
              </div>
            </CardHeading>

            <form onSubmit={handleSubmit}>
              <CardContent className="">
                <div className={styles.formContent}>
                  <Stack vertical spacing="m">
                    <Stack vertical spacing="xs">
                      <label htmlFor="email">
                        Email address*
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        required
                        placeholder="Enter your email"
                        disabled={loading}
                        className={styles.emailInput}
                      />
                    </Stack>

                    {errors.length > 0 && (
                      <Stack
                        vertical
                        spacing="xs"
                        padding="m"
                        className={styles.errorContainer}
                      >
                        {errors.map((error, index) => (
                          <Text key={index} bodySmall colour="error">
                            ❌ {error}
                          </Text>
                        ))}
                      </Stack>
                    )}

                    <Stack
                      padding="m"
                      className={styles.securityNote}
                    >
                      <Text bodySmall colour="default">
                        🔒 Your email is secure and will only be used for workshop access
                      </Text>
                    </Stack>
                  </Stack>
                </div>
              </CardContent>

              <CardFooter className="">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading || !email.trim()}
                >
                  {loading ? (
                    <Stack horizontal spacing="xs" alignItems="center" justifyContent="center">
                      <span> ⏳ Joining...</span>
                    </Stack>
                  ) : (
                    'Join meeting'
                  )}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </Stack>
    </div>
  );
};

export default AttendanceTracker;
